'use client'

import Link from 'next/link'
import { useState } from 'react'
import ChannelLogo from '@/components/live-tv/ChannelLogo'
import LiveTvPlayer from '@/components/live-tv/LiveTvPlayer'
import {
  LIVE_TV_CATEGORY_LABELS,
  LIVE_TV_CATEGORY_ORDER,
  type LiveTvChannel,
} from '@/lib/live-tv-channels'

function ChannelCard({ ch }: { ch: LiveTvChannel }) {
  return (
    <Link
      href={`/canli-tv/${ch.slug}`}
      prefetch={false}
      className="overflow-hidden rounded-[5px] border border-gray-200 bg-white p-3 hover:border-red-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-950"
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <ChannelLogo channel={ch} size="md" />
        <p className="w-full truncate text-sm font-bold text-gray-900 dark:text-white">
          {ch.title}
        </p>
        <p className="text-[11px] font-semibold text-red-600 dark:text-red-400">
          CANLI
        </p>
      </div>
    </Link>
  )
}

function groupByCategory(channels: LiveTvChannel[]) {
  return LIVE_TV_CATEGORY_ORDER.map((cat) => ({
    cat,
    channels: channels.filter((c) => c.category === cat),
  })).filter((g) => g.channels.length > 0)
}

export default function LiveTvWatch({
  active = null,
  embedVideoId = null,
  resolving = false,
  onAirChannels = [],
}: {
  active?: LiveTvChannel | null
  /** @handle/live sayfasından çözülen güncel video ID */
  embedVideoId?: string | null
  /** YouTube live ID çözülürken player alanında skeleton */
  resolving?: boolean
  /** Yalnızca şu an canlı yayında olan kanallar */
  onAirChannels?: LiveTvChannel[]
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const groups = groupByCategory(onAirChannels)

  if (!active) {
    return (
      <div className="space-y-8">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
          Canlı TV
        </h1>
        {groups.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Şu an canlı yayın yapan kanal yok. Kısa süre sonra tekrar deneyin.
          </p>
        ) : (
          groups.map(({ cat, channels }) => (
            <section key={cat} className="space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {LIVE_TV_CATEGORY_LABELS[cat]}
              </h2>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {channels.map((ch) => (
                  <ChannelCard key={ch.slug} ch={ch} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-[5px] border border-gray-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-950 sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={() => setSidebarOpen((v) => !v)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-50 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-900"
            aria-expanded={sidebarOpen}
            aria-controls="live-tv-channel-list"
          >
            Kanallar
          </button>
          <ChannelLogo channel={active} size="sm" />
          <h1 className="truncate text-base font-bold text-gray-900 dark:text-white sm:text-lg">
            {active.title} Canlı Yayını
          </h1>
        </div>
      </div>

      <div className="flex items-start gap-3">
        {sidebarOpen ? (
          <aside
            id="live-tv-channel-list"
            className="w-[168px] shrink-0 overflow-hidden rounded-[5px] border border-gray-200 bg-white sm:w-[200px] dark:border-slate-700 dark:bg-slate-950"
          >
            <div className="max-h-[min(70vh,560px)] overflow-y-auto overscroll-contain">
              {groups.map(({ cat, channels }) => (
                <div key={cat}>
                  <p className="sticky top-0 bg-gray-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-500 dark:bg-slate-900 dark:text-gray-400">
                    {LIVE_TV_CATEGORY_LABELS[cat]}
                  </p>
                  <ul>
                    {channels.map((ch) => {
                      const on = ch.slug === active.slug
                      return (
                        <li key={ch.key}>
                          <Link
                            href={`/canli-tv/${ch.slug}`}
                            prefetch={false}
                            className={`flex items-center gap-2 border-b border-gray-50 px-2 py-1.5 last:border-0 dark:border-slate-800/70 ${
                              on
                                ? 'bg-[#C02727] text-white'
                                : 'hover:bg-gray-50 dark:hover:bg-slate-900'
                            }`}
                          >
                            <ChannelLogo channel={ch} size="xs" />
                            <span
                              className={`min-w-0 flex-1 truncate text-[12px] font-semibold ${
                                on
                                  ? 'text-white'
                                  : 'text-gray-900 dark:text-gray-100'
                              }`}
                            >
                              {ch.title}
                            </span>
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </aside>
        ) : null}

        <section className="min-w-0 flex-1 overflow-hidden rounded-[5px] border border-gray-200 bg-black shadow-sm dark:border-slate-700">
          <div className="relative aspect-video w-full bg-black">
            {resolving ? (
              <div
                className="absolute inset-0 animate-pulse bg-zinc-800"
                aria-busy="true"
                aria-label="Yayın yükleniyor"
              />
            ) : embedVideoId ? (
              <LiveTvPlayer
                key={`${active.slug}-${embedVideoId}`}
                videoId={embedVideoId}
                title={`${active.title} canlı yayın`}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
                <p className="text-sm text-white/80">
                  Bu kanalda şu an canlı yayın yok.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
